import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-839" data-mp="il305x1xxykt">
        <p className="text-sm text-muted-foreground" data-id="element-840" data-mp="1f4acy0iqrp0n">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-841" data-mp="1aciivg0dn15r">
          <Button variant="secondary" data-id="element-842" data-mp="1aaxru0d9u0cn">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  function reorder(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    setSaving(true);
    setSaveError(null);
    try {
      await saveLanding(draft!);
      if (status) await setLandingStatus(draft!.id, status);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error desconocido.');
    } finally {
      setSaving(false);
    }
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-843" data-mp="pwfspb00bo35">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-844" data-mp="1ik23b37njszi" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-845" data-mp="14tgiva2jvazi">
        <div data-id="element-846" data-mp="18ua3nukp49o6">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-847" data-mp="18tqm4eyyjnao">
            {draft.name}
          </h1>
          <p className="text-sm text-muted-foreground" data-id="element-848" data-mp="1wijnws29gpd7">
            /landing/{draft.slug} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-849" data-mp="275q1pvwvanfp">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-850" data-mp="225c2929c0lta">
              Guardando…
            </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-851" data-mp="1x6sceqrmapvn">
              <CheckIcon className="h-4 w-4" data-id="element-852" data-mp="1s98p71cxz2rk" /> Guardado correctamente
            </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-853" data-mp="1nfxsvu5ra4dw">
              Cambios sin guardar
            </span>}
          <Button variant="ghost" onClick={() => handleSave('borrador')} loading={saving} data-id="element-854" data-mp="1egsvws9uej3u">
            Guardar borrador
          </Button>
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-856" data-mp="14imvyg7ljn1m" />} onClick={() => window.open(`/landing/${draft.slug}`, '_blank')} data-id="element-855" data-mp="19g4v5evtanwf">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-858" data-mp="zc7wg005rr5l" />} onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/landing/${draft.slug}`)} data-id="element-857" data-mp="ugskxepmi7cc">
            Copiar enlace
          </Button>
          {draft.status === 'publicada' ? <Button variant="destructive" onClick={() => handleSave('despublicada')} data-id="element-859" data-mp="bprpv1rmivy2">
              Despublicar
            </Button> : <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-860" data-mp="llkzoddykbc5">
              Publicar
            </Button>}
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-861" data-mp="3p2ur4iyefuwm">
          <span data-id="element-862" data-mp="1c2gsa7j4gk37">
            <strong className="font-semibold" data-id="element-863" data-mp="1c41nlzd9g724">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-864" data-mp="182q5eewpuo3y">
            Reintentar
          </Button>
        </div>}

      <div className="grid gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-865" data-mp="1h1blm3eptr1k">
        {/* Panel izquierdo */}
        <div className="min-w-0" data-id="element-866" data-mp="1v0jn52ycpb8x">
          <Tabs defaultValue="bloques" data-id="element-867" data-mp="1uzi0e17z30bd">
            <TabsList data-id="element-868" data-mp="1r12i2amu6g4a">
              <TabsTrigger value="bloques" data-id="element-869" data-mp="1qzhjm9xeid15">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-870" data-mp="24s79wqjyq442">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-871" data-mp="24ts249it1ayr">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-872" data-mp="1ztcrhppwqhkh">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-873" data-mp="2eplmug6ogarm">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-874" data-mp="2tdlqwk277zq6">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable onDragStart={() => setDragId(block.id)} onDragOver={e => e.preventDefault()} onDrop={() => reorder(block.id)} className={`rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-60' : ''}`} data-id="element-875" data-mp="2jgh2seoslu6q">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-876" data-mp="1qpz0vgzt1yuw">
                        <GripVerticalIcon className="h-4 w-4 flex-none cursor-grab text-muted-foreground" aria-hidden="true" data-id="element-877" data-mp="1qrs9h1tzat5j" />
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-878" data-mp="ua2qvawyx4cn">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-879" data-mp="p83ni118a7fp">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-880" data-mp="1hxat1jz3nxag">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-881" data-mp="18794kr2u4cv0">
                          <BookmarkIcon className="h-4 w-4" data-id="element-882" data-mp="3geuqg6p8q7dh" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-883" data-mp="3v3r0286y3sg7">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-884" data-mp="3be6s0zyomo98" /> : <EyeOffIcon className="h-4 w-4" data-id="element-885" data-mp="37dekcb5gkrf2" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-886" data-mp="moh138ycr3cn">
                          <CopyIcon className="h-4 w-4" data-id="element-887" data-mp="3phejreh686y1" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-888" data-mp="3thfb1zpdccmi">
                            <Trash2Icon className="h-4 w-4" data-id="element-889" data-mp="2e1gezmt1072m" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-890" data-mp="2air3izcx3g0r">
                          <Tabs defaultValue="contenido" data-id="element-891" data-mp="25kfu2pqjb7j2">
                            <TabsList data-id="element-892" data-mp="25iuxkuknteik">
                              <TabsTrigger value="contenido" data-id="element-893" data-mp="21iujxc5dx7ck">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-894" data-mp="1wk9gl3uu67jw">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-895" data-mp="1rlpxdb7mn9v8">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-896" data-mp="1rn9dn1ta9kx0" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-897" data-mp="1hv3mfuauv7iv">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-898" data-mp="1hvmu8i6skfj6" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-900" data-mp="33yn0i5mpjgf1" />} onClick={() => setAddOpen(true)} data-id="element-899" data-mp="13w3y6nr98un8">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-901" data-mp="2u7ikd2lappim">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-902" data-mp="2z74xzd51d7ry">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-903" data-mp="2l7dcysg3qr4g" />
                <Input id="l-slug" label="Slug" helperText={`${window.location.origin}/landing/${draft.slug}`} value={draft.slug} onChange={e => update({
                slug: e.target.value
              })} data-id="element-904" data-mp="2b9pim3merlmt" />
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-905" data-mp="egnp3tqdvwb3" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-906" data-mp="28qujlz2z78yy" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-907" data-mp="2rnc724askmpe" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-908" data-mp="xbfkopr7i717" />
                <div data-id="element-909" data-mp="1m2bht76q6ko6">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-910" data-mp="1qzbp1lfb9tgv">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-911" data-mp="1vzg9fmjcb8cd" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-912" data-mp="24yl6spj60vmy">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-913" data-mp="2er0l9cv97233">
                <div className="mb-3 flex items-center gap-2" data-id="element-914" data-mp="2jpu1ve5u797h">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-915" data-mp="2omar6borvtm5" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-916" data-mp="d09wayyc6l61">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-917" data-mp="30reop5wfbh5">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-918" data-mp="vposog32um43">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-919" data-mp="qrx51y9gtm5p">
                        <span className="min-w-0 flex-1" data-id="element-920" data-mp="h0sqv0rm65bx">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-921" data-mp="1eh9b4p9hxe3s">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-922" data-mp="19o6eg89f9ojd">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-923" data-mp="muzine3jnr3s">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-924" data-mp="11ob2ykjjntrz">
                          <Trash2Icon className="h-4 w-4" data-id="element-925" data-mp="3cigq6ncpv31q" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-926" data-mp="39x9985nj0g0t">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-927" data-mp="39w7mh3x5e539">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-928" data-mp="34yo7g1smloa9" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-929" data-mp="2m999ig88lfa3" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-930" data-mp="22l8t1ur2y8k7" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-931" data-mp="13xaw8esck5j9" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-932" data-mp="1mo2bfwz0mq5k" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-933" data-mp="1u8mprtcpe2nb" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-934" data-mp="1bbtu5qg2cppt" />
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-935" data-mp="346ut1i0l7o35" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-936" data-mp="3mxdnkm4w5z3p" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable */}
        <div className="h-[860px] min-w-0" data-id="element-937" data-mp="1xcomtvlrgbbw">
          <DevicePreview device={device} onDeviceChange={setDevice} toolbar={selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-939" data-mp="26eee91fn7a9o">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-940" data-mp="2gc2kanip8475">
                  Haz clic en una sección para editarla
                </span>} data-id="element-938" data-mp="1xdgkdbyx4j4t">
            <LandingRenderer landing={draft} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} data-id="element-941" data-mp="2l4vn35ulx2gl" />
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-942" data-mp="2z3bs8sctmkwm">
        {components.length > 0 && <div className="mb-6" data-id="element-943" data-mp="2ldqlfkttluj7">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-944" data-mp="2gh0atd0sbr56">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-945" data-mp="2v53c493vuenn">
              {components.map(component => <li key={component.id} data-id="element-946" data-mp="2q7bmxmumh6n5">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-947" data-mp="22gpdps8vtgbk">
                    <span className="block text-sm font-medium text-foreground" data-id="element-948" data-mp="27gbs450g52ly">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-949" data-mp="3nsqwpo55l5aa">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-950" data-mp="3vdjnjbb1xl2p">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-951" data-mp="3hfd615qetxp7">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-952" data-mp="3lgf0l99dtlku">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-953" data-mp="3lhgj1ysq9acl">
                <span className="block text-sm font-medium text-foreground" data-id="element-954" data-mp="479uwxk0qxzj">
                  {definition.label}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-955" data-mp="vyy45b7i34e5">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-957" data-mp="11kq7aaxgvjp2">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-958" data-mp="wmey82ezxd7w">Guardar componente</Button>
          </>} data-id="element-956" data-mp="1alwrov38lenl">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-959" data-mp="rmsoi2ydmf3u" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-960" data-mp="itpc7xzgw8as">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}